import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import geo from "@/data/meghalaya_districts.json";

// Colour by region
const REGION_HEX = {
  "Garo Hills": "#B4F8C8",
  "Khasi Hills": "#FFAEBC",
  "Jaintia Hills": "#A0E7E5",
};

const ID_TO_REGION = {
  "east-garo": "Garo Hills",
  "west-garo": "Garo Hills",
  "north-garo": "Garo Hills",
  "south-garo": "Garo Hills",
  "south-west-garo": "Garo Hills",
  "east-khasi": "Khasi Hills",
  "west-khasi": "Khasi Hills",
  "south-west-khasi": "Khasi Hills",
  "eastern-west-khasi": "Khasi Hills",
  "ri-bhoi": "Khasi Hills",
  "east-jaintia": "Jaintia Hills",
  "west-jaintia": "Jaintia Hills",
};

// Compute geographic bbox once (lon/lat)
const BBOX = (() => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (c) => {
    if (Array.isArray(c) && typeof c[0] === "number") {
      if (c[0] < minX) minX = c[0];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[1] > maxY) maxY = c[1];
      return;
    }
    if (Array.isArray(c)) c.forEach(walk);
  };
  geo.features.forEach((f) => walk(f.geometry.coordinates));
  return { minX, minY, maxX, maxY };
})();

// Simple equirectangular projection scaled to fit the box (top-down, north up)
function projectorFor(w, h, pad = 20) {
  const dx = BBOX.maxX - BBOX.minX;
  const dy = BBOX.maxY - BBOX.minY;
  const sx = (w - pad * 2) / dx;
  const sy = (h - pad * 2) / dy;
  const s = Math.min(sx, sy);
  const ox = (w - dx * s) / 2;
  const oy = (h - dy * s) / 2;
  return ([lon, lat]) => [ox + (lon - BBOX.minX) * s, oy + (BBOX.maxY - lat) * s];
}

// Convert a Polygon or MultiPolygon geometry to an SVG path using a projector
function geomToPath(geometry, project) {
  const rings = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  const parts = [];
  for (const poly of rings) {
    for (const ring of poly) {
      let s = "";
      ring.forEach(([lon, lat], i) => {
        const [x, y] = project([lon, lat]);
        s += (i === 0 ? "M" : "L") + x.toFixed(2) + "," + y.toFixed(2);
      });
      parts.push(s + "Z");
    }
  }
  return parts.join(" ");
}

function polyCentroid(geometry, project) {
  // Use outer ring vertex average of the largest polygon
  let bestRing = null, bestLen = -1;
  const polys = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  polys.forEach((poly) => {
    const outer = poly[0];
    if (outer && outer.length > bestLen) { bestRing = outer; bestLen = outer.length; }
  });
  if (!bestRing) return [0, 0];
  let sx = 0, sy = 0;
  bestRing.forEach((pt) => { const p = project(pt); sx += p[0]; sy += p[1]; });
  return [sx / bestRing.length, sy / bestRing.length];
}

export default function MeghalayaMap({ activeId, onHover, onSelect, tilted = false }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 380 });
  const [weatherCache, setWeatherCache] = useState({});
  const [hoveringId, setHoveringId] = useState(null);
  const [tip, setTip] = useState(null); // {x,y,id}

  // Resize observer
  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const ob = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      setSize({ w: cr.width, h: Math.max(320, cr.width * 0.48) });
    });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  const { paths, labels } = useMemo(() => {
    const project = projectorFor(size.w, size.h, 24);
    const paths = geo.features.map((f) => ({
      id: f.properties.id,
      name: f.properties.name,
      d: geomToPath(f.geometry, project),
      centroid: polyCentroid(f.geometry, project),
    }));
    return { paths, labels: paths };
  }, [size]);

  // Fetch weather lazily when hovered
  useEffect(() => {
    if (!hoveringId || weatherCache[hoveringId]) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/meghalaya/weather?district_id=${hoveringId}`);
        if (!cancelled) setWeatherCache((c) => ({ ...c, [hoveringId]: r.data }));
      } catch (e) {
        if (!cancelled) setWeatherCache((c) => ({ ...c, [hoveringId]: { error: true } }));
      }
    })();
    return () => { cancelled = true; };
  }, [hoveringId, weatherCache]);

  const setActive = (id, e) => {
    setHoveringId(id);
    onHover?.(id);
    if (e && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, id });
    }
  };

  const clearActive = () => {
    setHoveringId(null);
    setTip(null);
    onHover?.(null);
  };

  const tiltStyle = tilted
    ? { transform: "perspective(1200px) rotateX(38deg) rotateZ(-4deg)", transformStyle: "preserve-3d" }
    : {};

  const w = weatherCache[hoveringId];

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="megh-realmap-wrap">
      <motion.svg
        viewBox={`0 0 ${size.w} ${size.h}`}
        className="w-full h-auto"
        style={tiltStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        onMouseLeave={clearActive}
        data-testid="megh-realmap"
      >
        <defs>
          <pattern id="mapdots" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="1.2" cy="1.2" r="1" fill="#0A0A0A" opacity="0.06" />
          </pattern>
          <filter id="dropshadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="4" dy="4" stdDeviation="0" floodColor="#0A0A0A" />
          </filter>
        </defs>

        {/* Water backdrop */}
        <rect x="0" y="0" width={size.w} height={size.h} fill="url(#mapdots)" />

        {/* State outline shadow */}
        <g filter={tilted ? undefined : "url(#dropshadow)"}>
          {paths.map((p, i) => {
            const region = ID_TO_REGION[p.id] || "Khasi Hills";
            const isActive = activeId === p.id || hoveringId === p.id;
            return (
              <motion.path
                key={p.id}
                data-testid={`megh-real-${p.id}`}
                d={p.d}
                fill={REGION_HEX[region]}
                stroke="#0A0A0A"
                strokeWidth={isActive ? 3 : 1.4}
                strokeLinejoin="round"
                style={{ cursor: "pointer", transformOrigin: `${p.centroid[0]}px ${p.centroid[1]}px` }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: isActive ? 1.02 : 1 }}
                transition={{ delay: 0.04 * i, type: "spring", stiffness: 220, damping: 18 }}
                onMouseEnter={(e) => setActive(p.id, e)}
                onMouseMove={(e) => setActive(p.id, e)}
                onFocus={() => setActive(p.id)}
                onClick={() => onSelect?.(p.id)}
              />
            );
          })}
        </g>

        {/* District labels */}
        {labels.map((p) => (
          <g key={`l-${p.id}`} style={{ pointerEvents: "none" }}>
            <text
              x={p.centroid[0]}
              y={p.centroid[1]}
              textAnchor="middle"
              fontFamily="Outfit"
              fontWeight="800"
              fontSize={Math.max(9, Math.min(12, size.w / 80))}
              fill="#0A0A0A"
            >
              {shortName(p.name)}
            </text>
          </g>
        ))}
      </motion.svg>

      {/* Weather tooltip */}
      <AnimatePresence>
        {tip && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute pointer-events-none bg-white nb-border nb-shadow rounded-xl px-3 py-2 text-xs z-10"
            style={{ left: tip.x + 14, top: tip.y + 14, minWidth: 180 }}
            data-testid="megh-tip"
          >
            <div className="font-heading font-black text-sm tracking-tight">
              {geo.features.find((f) => f.properties.id === tip.id)?.properties.name}
            </div>
            <div className="font-mono-sl text-[10px] uppercase text-neutral-600 mb-1">click to open</div>
            {w && !w.error && (
              <div className="flex items-center gap-2">
                <span className="text-lg">{w.emoji}</span>
                <span className="font-heading font-bold">{Math.round(w.temp_c)}°C</span>
                <span className="text-neutral-600">{w.label}</span>
              </div>
            )}
            {w && w.error && <div className="text-neutral-500">weather offline</div>}
            {!w && <div className="text-neutral-500">loading weather…</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function shortName(n) {
  return n
    .replace("East ", "E. ")
    .replace("West ", "W. ")
    .replace("North ", "N. ")
    .replace("South ", "S. ")
    .replace("Eastern W. ", "E-W ")
    .replace(" Hills", "");
}
